import React, { useEffect, useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Input, Field, Loader, DatePicker } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PopupContainer,
  PageSecondaryTextStyles
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import countries from '@auth/components/countriesDropdown/farmersCountryList.json';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { LinkPopup } from '@applicationKYC/LegalEntityQuestionnaire/components/linkPopup/linkPopup';
import { Checkbox, RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components';
import { history } from '@/Config';
import { DropdownInput } from '@applicationKYC/FarmersQuestionnaire/components/dropdownInput/dropdownInput';
import { formatAndCleanPepData } from '@applicationKYC/FarmersQuestionnaire/utilities/clearPepInformation';
import { yesNoChipOptions } from '@applicationKYC/FarmersQuestionnaire/constants';
import { selectKycAppState } from '@redux';

const defaultValues = {
  isPep: null,
  isPersonPep: false,
  personPepCountry: null,
  personInstitution: null,
  personPosition: null,
  endOfPepStatus: null,
  isFamilyOrAssociatePep: false,
  familyOrAssociateName: null,
  familyOrAssociateSurname: null,
  familyOrAssociateBirthDate: null,
  familyOrAssociateCode: null,
  familyOrAssociateCountry: null,
  familyOrAssociateInstitution: null,
  familyOrAssociatePosition: null,
  familyOrAssociateEndOfPepStatus: null
};

const linkPopupGroup = {
  text: 'link_popup_who_is_pep_description_title',
  popupKey: 'link_popup_who_is_pep'
};

export const FarmerPepPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        isPep: Yup.boolean().required(t('validation_must_be_one_of')).nullable(),
        isPersonPep: Yup.boolean().nullable(),
        isFamilyOrAssociatePep: Yup.boolean().nullable(),
        personPepCountry: Yup.string()
          .nullable()
          .when(['isPep', 'isPersonPep'], {
            is: (isPep, isPersonPep) => isPep && isPersonPep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        personInstitution: Yup.string()
          .nullable()
          .when(['isPep', 'isPersonPep'], {
            is: (isPep, isPersonPep) => isPep && isPersonPep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        personPosition: Yup.string()
          .nullable()
          .when(['isPep', 'isPersonPep'], {
            is: (isPep, isPersonPep) => isPep && isPersonPep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        endOfPepStatus: Yup.date().typeError(t('validation_date_is_not_valid')).nullable(),
        familyOrAssociateName: Yup.string()
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        familyOrAssociateSurname: Yup.string()
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        familyOrAssociateBirthDate: Yup.date()
          .typeError(t('validation_date_is_not_valid'))
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.date()
              .max(new Date(), t('validation_date_entered_must_be_no_further_than_today'))
              .required(t('validation_required_field'))
              .typeError(t('validation_date_is_not_valid'))
              .nullable()
          }),
        familyOrAssociateCode: Yup.string()
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        familyOrAssociateCountry: Yup.string()
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        familyOrAssociateInstitution: Yup.string()
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        familyOrAssociatePosition: Yup.string()
          .nullable()
          .when(['isPep', 'isFamilyOrAssociatePep'], {
            is: (isPep, isFamilyOrAssociatePep) => isPep && isFamilyOrAssociatePep,
            then: Yup.string().nullable().required(t('validation_required_field'))
          }),
        familyOrAssociateEndOfPepStatus: Yup.date()
          .typeError(t('validation_date_is_not_valid'))
          .nullable()
      }).test('at-least-one-checkbox', t('validation_must_be_one_of'), function (values) {
        const { isPep, isPersonPep, isFamilyOrAssociatePep } = values;
        if (isPep && !isPersonPep && !isFamilyOrAssociatePep) {
          return this.createError({
            path: 'isPersonPep',
            message: t('validation_must_be_one_of')
          });
        }
        return true;
      }),
    [t]
  );

  const existingValues = farmerInformation?.farmerSection?.farmerPepPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const {
    validationHelpers,
    getValues,
    setTouchedOnAll,
    handleSubmit,
    setValues
  } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  useEffect(() => {
    const values = getValues();
    if (values.isPep === false) {
      setValues({ ...defaultValues, isPep: false });
    }
  }, [getValues, setValues]);

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        const formattedData = formatAndCleanPepData(data);
        await onSubmit(formattedData, 'farmerPepPage');
        history.push('/FarmersQuestionnaire?success=farmerSection');
      },
      () => setTouchedOnAll
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader>
        <PageHeader>{t('farmers_farmer_is_pep_header')}</PageHeader>
      </FarmersQuestionnaireHeader>
      <PopupContainer>
        <LinkPopup group={linkPopupGroup} farmers />
      </PopupContainer>
      <Field
        Component={RadioChipOption}
        options={yesNoChipOptions}
        value={values.isPep}
        name="isPep"
        validateOnChange
        validationHelpers={validationHelpers}
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      {values.isPep && (
        <>
          <PageSecondaryTextStyles>
            {t('farmers_farmer_is_pep_description')}
          </PageSecondaryTextStyles>
          <Field
            Component={Checkbox}
            value={values.isPersonPep}
            name="isPersonPep"
            checked={values.isPersonPep}
            validateOnChange
            validationHelpers={validationHelpers}
            label={t('farmers_farmer_is_pep_self_checkbox')}
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
          {values.isPersonPep && (
            <>
              <DropdownInput
                validationHelpers={validationHelpers}
                name="personPepCountry"
                value={values.personPepCountry}
                filterCountries={false}
                placeholder={t('farmers_farmer_is_pep_personPepCountry')}
                options={countries}
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={Input}
                name="personInstitution"
                value={values.personInstitution}
                placeholder={t('farmers_farmer_is_pep_personInstitution')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={Input}
                name="personPosition"
                value={values.personPosition}
                placeholder={t('farmers_farmer_is_pep_personPosition')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={DatePicker}
                name={'endOfPepStatus'}
                required
                showDisabledMonthNavigation
                value={values.endOfPepStatus?.toString()}
                placeholder={t('farmers_farmer_is_pep_endOfPepStatus')}
                validateOnBlur
                validateOnChange
                showYearDropdown
                yearDropdownItemNumber={99}
                scrollableYearDropdown
                validationHelpers={validationHelpers}
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
            </>
          )}
          <Field
            Component={Checkbox}
            name="isFamilyOrAssociatePep"
            checked={values.isFamilyOrAssociatePep}
            value={values.isFamilyOrAssociatePep}
            validationHelpers={validationHelpers}
            validateOnChange
            label={t('farmers_farmer_is_pep_family_checkbox')}
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
          {values.isFamilyOrAssociatePep && (
            <>
              <Field
                Component={Input}
                name="familyOrAssociateName"
                value={values.familyOrAssociateName}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateName')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={Input}
                name="familyOrAssociateSurname"
                value={values.familyOrAssociateSurname}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateSurname')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={DatePicker}
                name={'familyOrAssociateBirthDate'}
                required
                showDisabledMonthNavigation
                value={values.familyOrAssociateBirthDate?.toString()}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateBirthDate')}
                validateOnBlur
                validateOnChange
                showYearDropdown
                yearDropdownItemNumber={99}
                scrollableYearDropdown
                validationHelpers={validationHelpers}
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={Input}
                name="familyOrAssociateCode"
                value={values.familyOrAssociateCode}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateCode')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <DropdownInput
                validationHelpers={validationHelpers}
                name="familyOrAssociateCountry"
                value={values.familyOrAssociateCountry}
                filterCountries={false}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateCountry')}
                options={countries}
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={Input}
                name="familyOrAssociateInstitution"
                value={values.familyOrAssociateInstitution}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateInstitution')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={Input}
                name="familyOrAssociatePosition"
                value={values.familyOrAssociatePosition}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociatePosition')}
                validationHelpers={validationHelpers}
                required
                validateOnChange
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
              <Field
                Component={DatePicker}
                name={'familyOrAssociateEndOfPepStatus'}
                required
                showDisabledMonthNavigation
                value={values.familyOrAssociateEndOfPepStatus?.toString()}
                placeholder={t('farmers_farmer_is_pep_familyOrAssociateEndOfPepStatus')}
                validateOnBlur
                validateOnChange
                showYearDropdown
                yearDropdownItemNumber={99}
                scrollableYearDropdown
                validationHelpers={validationHelpers}
                disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
              />
            </>
          )}
        </>
      )}
      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary={true}
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
